import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionsAPI } from '../../shared/services/api';
import toast from 'react-hot-toast';

const SectionDialog = ({ 
  open, 
  onClose, 
  permissionId, 
  featureId,
  subFeatureId,
  section = null, 
  sectionId = null,
  isEdit = false 
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    displayName: ''
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => permissionsAPI.addSection(permissionId, featureId, subFeatureId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      toast.success('Section created successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create section');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => permissionsAPI.updateSection(permissionId, featureId, subFeatureId, sectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      toast.success('Section updated successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update section');
    },
  });

  // Load section data when editing
  useEffect(() => {
    if (isEdit && section) {
      setFormData({
        name: section.name || '',
        displayName: section.displayName || ''
      });
    } else {
      setFormData({
        name: '',
        displayName: ''
      });
    }
  }, [isEdit, section, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isEdit && sectionId) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEdit ? 'Edit Section' : 'Create Section'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Section Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              helperText="Unique identifier for the section (lowercase, no spaces)"
            />
            
            <TextField
              label="Display Name"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              required
              fullWidth
              helperText="Human-readable name shown in the UI"
            />
            

          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleClose}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending) ? (
              <CircularProgress size={20} />
            ) : (
              isEdit ? 'Update' : 'Create'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SectionDialog;
